import isNull from 'lodash/isNull';
import uniqBy from 'lodash/uniqBy';
import {asDecimalCurrency, asInteger} from 'utils/number-format';
import {reportingPost} from 'api';
import {fullMonthYearFormat} from 'hooks/config';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import ObjectUtils from '@spothero/utils/object';

/**
 * formats the revenue data for the MTD aggregated graph
 * @function formatMTDAggregatedRevenue
 * @param {Object<AccountingDashboardReportResponse>} data - The response data to format
 * @param {string} lastMonth - The name of the previous month
 * @param {string} currentMonth - The name of the current month
 * @returns {object} the formatted revenue data
 *
 * @memberof module:api/accounting/dto
 */
const formatMTDAggregatedRevenue = ({data, lastMonth, currentMonth}) => {
    const formattedRevenueData = data
        .reduce(
            (acc, revenueResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        remit: {mtd: previousRemitMTD},
                    },
                    remit: {mtd: currentRemitMTD},
                } = revenueResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousRemitMTD ? previousRemitMTD / 100 : 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentRemitMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: !isNull(currentRemitMTD)
                            ? currentRemitMTD / 100
                            : currentRemitMTD,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedRevenueData;
};

/**
 * formats the reservations data for the MTD aggregated graph
 * @function formatMTDAggregatedReservations
 * @param {object<AccountingDashboardReportResponse>} data - The response data to format
 * @param {string} lastMonth - The name of the previous month
 * @param {string} currentMonth - The name of the current month
 * @returns {object} the formatted reservations data
 *
 * @memberof module:api/accounting/dto
 */
const formatMTDAggregatedReservations = ({data, lastMonth, currentMonth}) => {
    const formattedReservationsData = data
        .reduce(
            (acc, reservationsResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        transactions: {mtd: previousTransactionsMTD},
                    },
                    transactions: {mtd: currentTransactionsMTD},
                } = reservationsResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousTransactionsMTD || 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentTransactionsMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: currentTransactionsMTD || 0,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedReservationsData;
};

/**
 * Formats the response from the dashboard endpoint
 * @function formatMTDAggregatedReport
 * @param {object<AccountingDashboardReportResponse>} data - The response data to format
 * @param {number} today - The current date dayjs object
 * @param {string} dateTo - The dateTo parameter representing the end of the date range queried
 * @returns {object} the formatted data
 *
 * @memberof module:api/accounting/dto
 */
const formatMTDAggregatedReport = ({data, dateTo}) => {
    const currentMonth = dayjs(dateTo).format(fullMonthYearFormat);
    const lastMonth = dayjs(dateTo)
        .subtract(1, 'month')
        .format(fullMonthYearFormat);

    const formatParams = {
        data,
        lastMonth,
        currentMonth,
    };

    const revenue = formatMTDAggregatedRevenue(formatParams);
    const reservations = formatMTDAggregatedReservations(formatParams);

    return {revenue, reservations};
};

/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @function
 * @param {object} data - The data to format
 * @return {AccountingDashboardReportSummary[]}
 *
 * @memberof module:api/accounting/dto
 */
export const formatGetMtdSummary = (data) => {
    const formattedData = ObjectUtils.camelizeKeys(data);

    const updatedData = {
        revenue: {
            value: asDecimalCurrency(formattedData?.remit.mtd / 100),
            previousValue: asDecimalCurrency(
                (formattedData?.previous.remit.mtd || 0) / 100
            ),
            diff: formattedData?.remit.change,
        },
        reservations: {
            value: asInteger(formattedData?.transactions.mtd),
            previousValue: asInteger(
                formattedData?.previous.transactions.mtd || 0
            ),
            diff: formattedData?.transactions.change,
        },
    };

    return updatedData;
};

/**
 * Gets the dashboard (summary cards) accounting data for a collection of facilities
 * @function
 * @param {AccountingReportRequest} data - The parameters for the report
 * @return {Promise<AccountingDashboardReportSummary>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/accounting/dashboard/'
 */
export const getMtdSummary = async (data, config = {}) => {
    try {
        const response = await reportingPost('dashboard/', data, config);

        return formatGetMtdSummary(response.data.data.mtd);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets a collection of aggregated MTD reports for a given set of facilities
 * @function
 * @param {AccountingReportRequest} data - The parameters for the report
 * @return {Promise<DashboardAggregatedResponse>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/accounting/facilities/mtd/aggregated/'
 */
export const getMTDAggregatedReport = async (data, config = {}) => {
    const {dateTo} = data;

    try {
        const response = await reportingPost(
            'facilities/mtd/aggregated/',
            data,
            config
        );

        const formattedResponse = formatMTDAggregatedReport({
            data: response.data.data,
            dateTo,
        });

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats a given MTD (Month-To-Date) report response by camelizing the
 * keys and rounding the current rating values to one decimal place.
 *
 * @function
 * @param {Object} response - The original response object to be formatted.
 * @returns {AccountingReportMTD[]} The formatted response with camelized keys and rating values rounded to one decimal place.
 *
 * @memberof module:api/accounting/dto
 */
const formatMTDReportResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);

    const formattedResults = formattedResponse.data.results.map((data) => ({
        ...data,
        currentRating: data.currentRating
            ? parseFloat(data.currentRating).toFixed(1)
            : null,
    }));

    const formattedData = {
        data: {
            results: formattedResults,
        },
        meta: formattedResponse.meta,
    };

    return formattedData;
};

/**
 * Gets an MTD (breakdown table) report for a given set of facilities
 * @function
 * @param {AccountingReportRequest} data - The parameters for the report
 * @return {Promise<AccountingReportMTD[]>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/accounting/facilities/mtd/'
 */
export const getMTDReport = async (data, config = {}) => {
    try {
        const response = await reportingPost('facilities/mtd/', data, config);
        const formattedResponse = formatMTDReportResponse(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
